// Events.js – публичный список событий с фильтром и пагинацией
import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useEventsList } from "../api/events";
import { usePostsList } from "../api/posts";
import SEO from "../components/SEO";
import { getImageUrl } from "../utils/imageUtils";
import { buildProfilePath } from "../utils/profileUrl";
import { CalendarDays, MapPin, User } from "lucide-react";

function formatDateTime(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        //    hour: "2-digit",
        //   minute: "2-digit",
    });
}

const Events = () => {
    const [params, setParams] = useSearchParams();
    const q = (params.get("q") || "").trim();
    const city = (params.get("city") || "").trim();
    const page = Math.max(parseInt(params.get("page") || "1", 10), 1);

    const [includePast, setIncludePast] = useState(false);
    const [typeFilter, setTypeFilter] = useState(params.get("type") || "all"); // "all" | "events" | "posts"

    const queryParams = useMemo(() => {
        const out = { page, limit: 20 };
        if (q) out.q = q;
        if (city) out.city = city;
        if (includePast) out.includePast = 1;
        return out;
    }, [q, city, page, includePast]);

    // Events- und Posts-API separat abfragen
    const { data: eventsData, isLoading: loadingEvents, error: errorEvents } = useEventsList(typeFilter !== "posts" ? queryParams : null);
    const { data: postsData, isLoading: loadingPosts, error: errorPosts } = usePostsList(typeFilter !== "events" ? { ...queryParams } : null);

    // Memoize to keep stable references (prevents unnecessary re-renders / hook-deps warnings)
    const events = useMemo(() => eventsData?.events || [], [eventsData]);
    const posts = useMemo(() => postsData?.posts || [], [postsData]);

    // Kombinierter Feed (sortiert nach Datum, posts: published_at, events: start_at)
    const combinedFeed = useMemo(() => {
        let items = [];
        if (typeFilter === "all") {
            items = [
                ...events.map(ev => ({ ...ev, _type: "event", _date: ev.start_at })),
                ...posts.map(post => ({ ...post, _type: "post", _date: post.published_at }))
            ];
        } else if (typeFilter === "events") {
            items = events.map(ev => ({ ...ev, _type: "event", _date: ev.start_at }));
        } else if (typeFilter === "posts") {
            items = posts.map(post => ({ ...post, _type: "post", _date: post.published_at }));
        }
        // Sortiere absteigend nach Datum
        return items
            .filter(item => !!item._date)
            .sort((a, b) => new Date(b._date) - new Date(a._date));
    }, [events, posts, typeFilter]);

    const isLoading = (typeFilter === "all" && (loadingEvents || loadingPosts))
        || (typeFilter === "events" && loadingEvents)
        || (typeFilter === "posts" && loadingPosts);

    const error = errorEvents || errorPosts;

    const pagination = eventsData?.pagination || postsData?.pagination || { page, limit: 20, total: 0, totalPages: 1 };

    const onChangeQ = (val) => {
        const next = new URLSearchParams(params);
        if (val) next.set("q", val);
        else next.delete("q");
        next.set("page", "1");
        setParams(next, { replace: false });
    };

    const onChangeCity = (val) => {
        const next = new URLSearchParams(params);
        if (val) next.set("city", val);
        else next.delete("city");
        next.set("page", "1");
        setParams(next, { replace: false });
    };

    const onPageChange = (nextPage) => {
        const next = new URLSearchParams(params);
        next.set("page", String(nextPage));
        setParams(next, { replace: false });
    };

    const onTypeFilterChange = (val) => {
        setTypeFilter(val);
        const next = new URLSearchParams(params);
        if (val && val !== "all") next.set("type", val);
        else next.delete("type");
        next.set("page", "1");
        setParams(next, { replace: false });
    };

    return (
        <>
            <SEO
                title="События — все мероприятия"
                description="Все опубликованные события и мероприятия артистов на платформе, отсортированные по дате."
                url="/events"
                type="website"
                canonical="/events"
            />
            <div className="max-w-7xl mx-auto px-4 py-8 min-h-screen">
                <h1 className="text-3xl font-bold mb-4 flex items-center gap-2">
                    <CalendarDays className="w-7 h-7 text-primary-600" />
                    События и мероприятия
                </h1>
                <div className="mb-6 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <input
                        type="text"
                        className="border bg-white rounded-lg px-3 py-2 w-full md:w-64"
                        placeholder="Поиск по названию, площадке, городу…"
                        value={q}
                        onChange={(e) => onChangeQ(e.target.value)}
                    />
                    <input
                        type="text"
                        className="border bg-white rounded-lg px-3 py-2 w-full md:w-48"
                        placeholder="Фильтр по городу"
                        value={city}
                        onChange={(e) => onChangeCity(e.target.value)}
                    />
                    <label className="inline-flex items-center gap-2 text-sm ml-1">
                        <input
                            type="checkbox"
                            checked={includePast}
                            onChange={(e) => setIncludePast(e.target.checked)}
                        />
                        Показывать прошедшие
                    </label>

                    {/* UX: segmented buttons instead of select */}
                    <div className="inline-flex w-full md:w-auto rounded-lg border bg-white overflow-hidden">
                        {[
                            { value: "all", label: "Все" },
                            { value: "events", label: "События" },
                            { value: "posts", label: "Посты" },
                        ].map((opt) => {
                            const active = typeFilter === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => onTypeFilterChange(opt.value)}
                                    aria-pressed={active}
                                    className={
                                        "flex-1 md:flex-none px-4 py-2 text-sm font-medium transition " +
                                        (active
                                            ? "bg-primary-600 text-white"
                                            : "bg-white text-secondary-800 hover:bg-secondary-50")
                                    }
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                {isLoading && (
                    <div className="py-12 text-center text-secondary-600">Загрузка…</div>
                )}
                {error && (
                    <div className="py-12 text-center text-red-600">Ошибка при загрузке.</div>
                )}
                {!isLoading && !error && combinedFeed.length === 0 && (
                    <div className="py-12 text-center text-secondary-600">Ничего не найдено.</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {combinedFeed.map((item) =>
                        item._type === "event" ? (
                            <Link
                                    to={`/events/${item.id}`}
                                    aria-label={item.title}
                                
                                >
                            <div
                                key={"event-" + item.id}
                                className="relative card hover:shadow-lg transition p-4 card-event"
                            >
                                {/* Full-card click target (no nested Links) */}
                                

                                <div className="relative z-20 flex flex-col md:flex-row gap-4">
                                    
                                    {item.image_url && (
                                        <div className="md:w-48 md:flex-shrink-0">
                                            <img
                                                src={getImageUrl(item.image_url)}
                                                alt={item.title}
                                                className="w-full object-cover rounded-lg"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}

                                    <div className="min-w-0 flex-1 flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-sm text-secondary-700">
                                            <CalendarDays className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">
                                                {formatDateTime(item.start_at)}
                                                {item.end_at && <>– {formatDateTime(item.end_at)}</>}
                                            </span>
                                        </div>
                                        <div className="font-bold text-lg text-secondary-900 line-clamp-2">
                                            {item.title}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-secondary-700">
                                            <MapPin className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">
                                                {item.venue && <>{item.venue}, </>}
                                                {item.city}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-secondary-700">
                                            <User className="w-4 h-4 flex-shrink-0" />
                                            <Link
                                                to={buildProfilePath({
                                                    profile: {
                                                        username_slug: item.owner?.username_slug,
                                                        username: item.owner?.username,
                                                        id: item.owner_user_id,
                                                    },
                                                    categoryPath: {
                                                        main: { slug: item.owner?.category?.main_slug },
                                                        sub: { slug: item.owner?.category?.sub_slug },
                                                        subSub: { slug: item.owner?.category?.sub_sub_slug },
                                                    },
                                                })}
                                                className="relative z-30 hover:underline"
                                            >
                                                {item.owner?.title || item.owner?.username}
                                            </Link>
                                        </div>

                                        {item.tickets_url && (
                                            <a
                                                href={item.tickets_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="relative z-30 mt-1 inline-block text-primary-700 hover:underline text-sm"
                                            >
                                                Билеты &rarr;
                                            </a>
                                        )}
                                    </div>
                                </div>        
                            </div>
                            </Link>
                        ) : (
                            
                            <div
                                key={"post-" + item.id}
                                className="relative card hover:shadow-lg transition p-4 card-blog"
                            >
                               {/* Card should be clickable to post details */}
                                {item.slug && (
                                    <Link
                                        to={`/posts/${item.slug}`}
                                        aria-label={item.title || item.body}
                                        
                                    > 
                                

                                <div className="relative z-20 flex flex-col md:flex-row gap-4">
                                    {item.image_url && (
                                        <div className="md:w-48 md:flex-shrink-0">
                                            <img
                                                src={getImageUrl(item.image_url)}
                                                alt={item.title || "Post"}
                                                className="w-full object-cover rounded-lg"
                                                loading="lazy"
                                            />
                                        </div>
                                    )}

                                    <div className="min-w-0 flex-1 flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-sm text-secondary-700">
                                            <CalendarDays className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">{formatDateTime(item.published_at)}</span>
                                        </div>

                                        <div className="font-bold text-lg text-secondary-900 line-clamp-2">
                                            {item.title || item.body}
                                        </div>
                                        {item.body && (
                                            <div className="text-sm text-secondary-700 line-clamp-3">
                                                {item.title ? item.body : null}
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 text-sm text-secondary-700">
                                            <User className="w-4 h-4 flex-shrink-0" />
                                            <Link
                                                to={buildProfilePath({
                                                    profile: {
                                                        username_slug: item.owner?.username_slug,
                                                        username: item.owner?.username,
                                                        id: item.owner_user_id,
                                                    },
                                                    categoryPath: {
                                                        main: { slug: item.owner?.category?.main_slug },
                                                        sub: { slug: item.owner?.category?.sub_slug },
                                                        subSub: { slug: item.owner?.category?.sub_sub_slug },
                                                    },
                                                })}
                                                className="relative z-30 hover:underline"
                                            >
                                                {item.owner?.title || item.owner?.username}
                                            </Link>
                                        </div>

                                        {item.link_url && (
                                            <a
                                                href={item.link_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="relative z-30 mt-1 inline-block text-primary-700 hover:underline text-sm break-all"
                                            >
                                                {item.link_url}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                
                                
                               </Link>
                        )} 
                            </div>
                        ) 

                    )}
                </div>
                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex justify-center mt-8 gap-2">
                        {Array.from({ length: pagination.totalPages }, (_, i) => (
                            <button
                                key={i + 1}
                                className={`px-3 py-1 rounded ${pagination.page === i + 1
                                    ? "bg-primary-600 text-white"
                                    : "bg-white border text-secondary-800"
                                    }`}
                                onClick={() => onPageChange(i + 1)}
                                disabled={pagination.page === i + 1}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default Events;
